import React from 'react';
import gpaCalculatorServices from 'services/gpaCalculator.services';
import { A_F_Grade, genGradeOptions } from './formCreateGpaForecastModal';
import { gpaHeroCardTokens as T } from './gpaHeroCard.tokens';

/**
 * GPA Calculator Revamp v1.1 (ClickUp #86ak9ab4g) — Step 5 + 6 + CSU expansion.
 *
 * DevSpec Section 6 UI: 6 tiles grouped HS · UC · CSU (added 2026-09-19 —
 * expanded from Vuong's original 4-tile UC/CSU-merged version after DevSpec
 * update split UC and CSU into separate modes). Wired to
 * `POST /api/gpa/calculate` (backend Step 4). Tap-through on a tile opens a
 * breakdown modal listing every course that fed into that mode's GPA sum.
 *
 * Six modes (backend enum GpaMode 1-6):
 *   1 HS Unweighted   |   2 HS Weighted           - full 9-12
 *   3 UC-Capped       |   4 UC-Uncapped           - 10-11 + qualifying summer
 *   5 CSU-Capped      |   6 CSU-Uncapped          - 10-12 completed terms
 *
 * CSU differs from UC in three DevSpec-documented ways (verified against
 * calstate.edu/apply/gpa-calculator):
 *   1. Grade-year window includes 12th grade completed terms (UC excludes).
 *   2. Sophomore sublimit is 2 semester bonus points (UC allows 4).
 *   3. UC-transferable college courses count 2x (not observable in this pass
 *      because college courses are out of scope; see backend types.ts header).
 *
 * Deliberately self-contained (plain inline styles + local tokens file, no
 * antd/MUI/bootstrap dependency) so it drops into both the student page
 * (GpaForecaster.tsx, Bootstrap) and the counselor page (RGPAForecaster.tsx,
 * MUI) without fighting either. Inline hex was swapped for tokens (see
 * gpaHeroCard.tokens.ts) on 2026-09-19 so future refactors to a global tokens
 * module are search-and-replace.
 *
 * Rollout gate: the backend gpa-calculator.service.ts only computes and
 * returns a result when `student.gpa_version === 2` — every student defaults
 * to `gpa_version = 1`. When gated off, `/api/gpa/calculate` returns
 * `{ enabled: false }` and this component renders nothing at all, so it is
 * safe to always mount. Does NOT touch the existing GpaForecaster table/state
 * ("minimal touch" per DevSpec Step 5).
 *
 * Step 6 what-if: forecast preview panel lets a student pick one of the
 * backend's `forecastableCourses` (in-progress / planned / etc — see
 * gpa-calculator.ts) plus a hypothetical grade, and shows all 6 modes
 * recomputed side-by-side with the current numbers. Nothing is persisted —
 * only calls `POST /api/gpa/calculate-forecast`, never the write path
 * (`PUT /api/grade-and-college-course`).
 */

// ---------- mode metadata (single source of truth for the 4 tiles) ----------
//
// Consolidated 2026-09-19 evening per Michelle after her UC counselor breakout
// session with Berkeley/UCSB/Merced: tiles are NOT attributed to specific
// college systems. Four calculation methods, one banner explaining that
// colleges use different subsets in review. The old UC/CSU-split into 6 tiles
// is gone — see types.ts GpaMode enum comment for the full rationale.

const MODE_ORDER = [1, 2, 3, 4] as const;

const MODE_LABELS: Record<number, string> = {
  1: 'Unweighted',
  2: 'Weighted',
  3: 'Capped',
  4: 'Uncapped',
};

const MODE_LONG_LABELS: Record<number, string> = {
  1: 'Unweighted GPA',
  2: 'Weighted GPA',
  3: 'Capped GPA',
  4: 'Uncapped GPA',
};

/**
 * Plain-English tile-scope copy. Each line explains, in one sentence, which
 * grades count and how the bonus rule works. Do not shorten in the name of a
 * cleaner look — these are the reason a counselor understands "why 4.25 vs 4.42."
 */
const TILE_SCOPE: Record<number, string> = {
  1: 'All 9th-12th grade courses. No bonus for weighted courses.',
  2: 'All 9th-12th grade courses. +1 bonus for each weighted course. No cap.',
  3: '10th-12th grade A-G courses. Weighted bonus capped at 8 semesters.',
  4: 'Same 10th-12th A-G window as Capped. Every weighted semester gets full +1 bonus (no cap).',
};

const CAPPED_MODES = new Set<number>([3]);

/**
 * Verbatim from the v5.5 dashboard so the forecaster and the dashboard use the
 * same words. Locked to prevent drift. Line breaks kept exactly.
 */
const CALCULATION_NOTE =
  'These are the four ways a GPA gets calculated: unweighted and weighted across 9-12, and capped and uncapped for the UC and CSU window. Colleges recalculate and weigh GPA differently, and it varies from campus to campus, even within the same system. Some review just one of these, others look at several or all of them when reading an application, so we show them all.';

const ELIGIBILITY_MINIMUMS = 'UC min 3.0 CA / 3.4 out of state · CSU min 2.5 CA / 3.0 out of state';

// ---------- backend response types (mirror of student-backend/src/gpa-forecast/calculator/types.ts) ----------

interface ContributingCourse {
  gpaForecastId: number;
  course: string;
  term: string;
  schoolName: string;
  rawGrade: string;
  gradePoint: number;
  bonusApplied: boolean;
  credit: number;
}

interface ModeResult {
  gpa: number | null;
  bonusCapReached?: boolean;
  courseCount: number;
  perSchool: Record<string, { gpa: number | null; courseCount: number }>;
  contributingCourses: ContributingCourse[];
}

interface ForecastableCourse {
  gpaForecastId: number;
  course: string;
  term: string;
  schoolName: string;
  rawGrade: string;
  gradingScale: string;
}

interface GpaCalculatorResult {
  modes: Record<string, ModeResult>;
  auxiliary: {
    weightedCourseCount: number;
    aGCounts: null;
    hsOnlyGpa: number | null;
    hsOnlyCourseCount: number;
    collegeOnlyGpa: number | null;
    collegeOnlyCourseCount: number;
    collegeUnitsTotal: number;
    /** CSU-variant Capped/Uncapped: same calc as Modes 3/4 but with college coursework at 2x weight. Null when student has no college coursework. */
    cappedWithCsuCollegeWeight: number | null;
    uncappedWithCsuCollegeWeight: number | null;
  };
  warnings: string[];
  forecastableCourses: ForecastableCourse[];
}

type GpaCalculatorResponse = { enabled: false } | ({ enabled: true } & GpaCalculatorResult);

/** One pending what-if entry — a course + a hypothetical grade, not yet saved anywhere. */
interface WhatIfEntry {
  gpaForecastId: number;
  term: string;
  course: string;
  grade: string;
}

interface Props {
  studentId: string | number | null | undefined;
}

// ---------- shared inline style objects ----------

const cardStyle: React.CSSProperties = {
  background: T.surface,
  borderRadius: T.radiusSurface,
  padding: T.space5,
  marginBottom: T.space5,
  boxShadow: T.elevSurface,
  fontFamily: T.fontUi,
  color: T.ink,
};

// const groupLabelStyle: React.CSSProperties = {
//   fontSize: T.textXs,
//   fontWeight: 600,
//   letterSpacing: '0.05em',
//   color: T.inkMuted,
//   marginBottom: T.space2,
// };

const tileStyle: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: '130px',
  background: T.surface,
  border: `1px solid ${T.hairline}`,
  borderRadius: T.radiusNested,
  padding: `${T.space3} ${T.space3}`,
  textAlign: 'center',
  cursor: 'pointer',
  position: 'relative',
};

const tileNumberStyle: React.CSSProperties = {
  fontSize: T.text2xl,
  fontWeight: 700,
  color: T.ink,
  lineHeight: 1.15,
};

const tileProjectedStyle: React.CSSProperties = {
  fontSize: T.textLg,
  fontWeight: 600,
  color: T.accentInk,
  marginLeft: '4px',
};

const tileLabelStyle: React.CSSProperties = {
  fontSize: T.textSm,
  color: T.inkMuted,
  marginTop: T.space1,
  fontWeight: 600,
};

const tileScopeStyle: React.CSSProperties = {
  fontSize: T.textXs,
  color: T.inkMuted,
  marginTop: T.space2,
  paddingTop: T.space2,
  borderTop: `1px dashed ${T.hairline}`,
  lineHeight: 1.35,
  textAlign: 'left',
};

const capChipStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: T.textXs,
  color: T.warningInk,
  background: T.warningWash,
  padding: '2px 6px',
  borderRadius: T.radiusControl,
  marginTop: T.space1,
};

const chipStyle = (active: boolean): React.CSSProperties => ({
  fontSize: T.textXs,
  padding: '4px 10px',
  borderRadius: T.radiusControl,
  cursor: 'pointer',
  background: active ? T.accentTint : T.sunken,
  color: active ? T.accentInk : T.inkStrong,
  fontWeight: active ? 600 : 500,
  border: `1px solid ${active ? T.accentTint : 'transparent'}`,
});

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: T.scrim,
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: T.fontUi,
};

const modalStyle: React.CSSProperties = {
  background: T.surface,
  borderRadius: T.radiusSurface,
  padding: T.space5,
  maxWidth: '640px',
  width: '92%',
  maxHeight: '80vh',
  overflowY: 'auto',
  boxShadow: T.elevOverlay,
  color: T.ink,
};

// ---------- helpers ----------

const formatGpa = (gpa: number | null | undefined) =>
  gpa === null || gpa === undefined ? '-' : gpa.toFixed(2);

/**
 * Whether the CSU-variant footnote is meaningful for this student. Only true
 * when there's actual college coursework AND the with-CSU-2x-weight value
 * differs from the standard Capped/Uncapped by a meaningful amount.
 */
const shouldShowCsuVariant = (result: GpaCalculatorResult | null): boolean => {
  if (!result) return false;
  const capped = result.modes?.['3']?.gpa;
  const csuCapped = result.auxiliary?.cappedWithCsuCollegeWeight;
  if (capped === null || capped === undefined || csuCapped === null || csuCapped === undefined) return false;
  return Math.abs(csuCapped - capped) >= 0.005;
};

/** Collect every school that appears in any mode's per-school breakdown. */
const collectSchoolNames = (result: GpaCalculatorResult | null): string[] => {
  if (!result) return [];
  const set = new Set<string>();
  for (const mode of Object.values(result.modes)) {
    for (const school of Object.keys(mode.perSchool ?? {})) set.add(school);
  }
  return Array.from(set).sort();
};

/**
 * Per-mode GPA to display for the currently-selected school view. When
 * `All schools` is selected, that's the mode's overall gpa; otherwise it's
 * the perSchool[school].gpa. Falls back to overall gpa if a per-school entry
 * is missing (shouldn't happen but keeps the tile from showing "—" for a
 * transient race).
 */
const gpaForSchool = (mode: ModeResult | undefined, schoolFilter: string): number | null => {
  if (!mode) return null;
  if (schoolFilter === '') return mode.gpa;
  const perSchool = mode.perSchool?.[schoolFilter];
  return perSchool ? perSchool.gpa : mode.gpa;
};

// ---------- component ----------

const GPAHeroCard = ({ studentId }: Props) => {
  const [loading, setLoading] = React.useState(true);
  const [response, setResponse] = React.useState<GpaCalculatorResponse | null>(null);
  const [openMode, setOpenMode] = React.useState<number | null>(null);
  const [schoolFilter, setSchoolFilter] = React.useState<string>('');
  const [showNormalize, setShowNormalize] = React.useState(false);

  // Step 6 — what-if state.
  const [whatIfEntries, setWhatIfEntries] = React.useState<WhatIfEntry[]>([]);
  const [selectedCourseKey, setSelectedCourseKey] = React.useState('');
  const [selectedGrade, setSelectedGrade] = React.useState('');
  const [whatIfResponse, setWhatIfResponse] = React.useState<GpaCalculatorResponse | null>(null);
  const [whatIfLoading, setWhatIfLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!studentId) return;
      setLoading(true);
      try {
        const res: any = await gpaCalculatorServices.calculate(studentId);
        if (!cancelled) setResponse(res);
      } catch (ex) {
        console.log('GPAHeroCard: failed to load /api/gpa/calculate', ex);
        if (!cancelled) setResponse({ enabled: false });
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (whatIfEntries.length === 0) {
        setWhatIfResponse(null);
        return;
      }
      setWhatIfLoading(true);
      try {
        const plannedGrades = whatIfEntries.map((e) => ({
          gpa_forecast_id: e.gpaForecastId,
          term: e.term,
          grade: e.grade,
        }));
        const res: any = await gpaCalculatorServices.calculateForecast(studentId, plannedGrades);
        if (!cancelled) setWhatIfResponse(res);
      } catch (ex) {
        console.log('GPAHeroCard: failed to load /api/gpa/calculate-forecast', ex);
        if (!cancelled) setWhatIfResponse(null);
      }
      if (!cancelled) setWhatIfLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [whatIfEntries, studentId]);

  if (!studentId) return null;
  if (loading) return null;
  if (!response || !response.enabled) return null;

  const result: GpaCalculatorResult = response;
  const openModeResult = openMode !== null ? result?.modes?.[String(openMode)] : undefined;
  const whatIfResult = whatIfResponse && whatIfResponse.enabled ? whatIfResponse : null;
  const schoolNames = collectSchoolNames(result);
  const hasMultipleSchools = schoolNames.length > 1;
  const showCsuFootnote = shouldShowCsuVariant(result);

  // Step 6 — what-if helpers.
  const forecastableCourses = result.forecastableCourses ?? [];
  const courseKey = (c: { gpaForecastId: number; term: string }) => `${c.gpaForecastId}|${c.term}`;
  const selectedCourse = forecastableCourses.find((c) => courseKey(c) === selectedCourseKey);
  const gradeOptionsForSelected: any[] = selectedCourse
    ? (() => {
        const opts = genGradeOptions(selectedCourse.gradingScale);
        return opts.length > 0 ? opts : A_F_Grade;
      })()
    : A_F_Grade;

  const handleAddWhatIf = () => {
    if (!selectedCourse || !selectedGrade) return;
    setWhatIfEntries((prev) => {
      const withoutExisting = prev.filter((e) => courseKey(e) !== selectedCourseKey);
      return [
        ...withoutExisting,
        { gpaForecastId: selectedCourse.gpaForecastId, term: selectedCourse.term, course: selectedCourse.course, grade: selectedGrade },
      ];
    });
    setSelectedGrade('');
  };

  const handleRemoveWhatIf = (key: string) => {
    setWhatIfEntries((prev) => prev.filter((e) => courseKey(e) !== key));
  };

  return (
    <div style={cardStyle}>
      {/* School switcher — only shown for multi-school students */}
      {hasMultipleSchools ? (
        <div style={{ display: 'flex', gap: T.space2, marginBottom: T.space4, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: T.textXs, color: T.inkMuted, fontWeight: 600 }}>Showing:</span>
          <span style={chipStyle(schoolFilter === '')} onClick={() => setSchoolFilter('')}>
            All schools
          </span>
          {schoolNames.map((school) => (
            <span key={school} style={chipStyle(schoolFilter === school)} onClick={() => setSchoolFilter(school)}>
              {school}
            </span>
          ))}
        </div>
      ) : null}

      {/* Four tiles in a single row, no HS/UC/CSU grouping */}
      <div style={{ display: 'flex', gap: T.space2, flexWrap: 'wrap' }}>
        {MODE_ORDER.map((mode) => {
          const modeResult = result?.modes?.[String(mode)];
          const displayGpa = gpaForSchool(modeResult, schoolFilter);
          const whatIfDisplayGpa = whatIfResult
            ? gpaForSchool(whatIfResult.modes?.[String(mode)], schoolFilter)
            : null;
          const showProjected =
            whatIfDisplayGpa !== null &&
            displayGpa !== null &&
            Math.abs((whatIfDisplayGpa as number) - (displayGpa as number)) >= 0.005;

          return (
            <div
              key={mode}
              style={tileStyle}
              onClick={() => modeResult && setOpenMode(mode)}
              role="button"
              aria-label={`${MODE_LONG_LABELS[mode]}, tap for breakdown`}
            >
              <div>
                <span style={tileNumberStyle}>{formatGpa(displayGpa)}</span>
                {showProjected ? (
                  <span style={tileProjectedStyle}>→ {formatGpa(whatIfDisplayGpa)}</span>
                ) : null}
              </div>
              <div style={tileLabelStyle}>{MODE_LABELS[mode]}</div>
              {CAPPED_MODES.has(mode) && modeResult?.bonusCapReached ? (
                <div style={capChipStyle}>Cap applied</div>
              ) : null}
              <div style={tileScopeStyle}>{TILE_SCOPE[mode]}</div>
            </div>
          );
        })}
      </div>

      {/* Eligibility minimums line (published thresholds, not attributions) */}
      <div style={{ marginTop: T.space3, fontSize: T.textXs, color: T.inkMuted, textAlign: 'center' }}>
        {ELIGIBILITY_MINIMUMS}
      </div>

      {/* Calculation-note banner — verbatim from v5.5 dashboard */}
      <div
        style={{
          marginTop: T.space3,
          padding: T.space3,
          background: T.nested,
          borderRadius: T.radiusNested,
          fontSize: T.textSm,
          color: T.inkBody,
          lineHeight: 1.55,
          border: `1px solid ${T.hairline}`,
        }}
      >
        {CALCULATION_NOTE}
      </div>

      {/* CSU-variant footnote — only when the student has college coursework AND the with-CSU-2x-weight value differs */}
      {showCsuFootnote && result.auxiliary ? (
        <div
          style={{
            marginTop: T.space2,
            padding: T.space2,
            background: T.accentWash,
            borderRadius: T.radiusControl,
            fontSize: T.textXs,
            color: T.inkBody,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: T.inkStrong }}>Some Cal State schools weigh college coursework 2x.</strong> With
          that rule applied, this student's Capped is{' '}
          <strong>{formatGpa(result.auxiliary.cappedWithCsuCollegeWeight)}</strong> and Uncapped is{' '}
          <strong>{formatGpa(result.auxiliary.uncappedWithCsuCollegeWeight)}</strong>.
        </div>
      ) : null}

      {/* Auxiliary stats row — weighted-course count + source-split HS-only/college-only GPAs + normalize link */}
      {result?.auxiliary ? (
        <div style={{ fontSize: T.textXs, color: T.inkMuted, marginTop: T.space3, display: 'flex', flexWrap: 'wrap', gap: T.space2, alignItems: 'baseline' }}>
          <span>
            <strong style={{ color: T.inkStrong }}>{result.auxiliary.weightedCourseCount}</strong> weighted courses
          </span>
          {result.auxiliary.hsOnlyGpa !== null ? (
            <>
              <span>·</span>
              <span>
                HS-only GPA: <strong style={{ color: T.inkStrong }}>{formatGpa(result.auxiliary.hsOnlyGpa)}</strong>
              </span>
            </>
          ) : null}
          {result.auxiliary.collegeOnlyGpa !== null ? (
            <>
              <span>·</span>
              <span>
                College-only GPA: <strong style={{ color: T.inkStrong }}>{formatGpa(result.auxiliary.collegeOnlyGpa)}</strong>
                <span style={{ marginLeft: '4px', color: T.inkMuted }}>({result.auxiliary.collegeUnitsTotal} units)</span>
              </span>
            </>
          ) : null}
          <span>·</span>
          <span
            style={{ color: T.accentInk, cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setShowNormalize((v) => !v)}
          >
            {showNormalize ? 'Hide' : 'How we normalize'}
          </span>
        </div>
      ) : null}

      {/* Warnings */}
      {result?.warnings && result.warnings.length > 0 ? (
        <div style={{ fontSize: T.textXs, color: T.warningInk, marginTop: T.space2 }}>
          {result.warnings.map((w, i) => (
            <div key={i}>⚠ {w}</div>
          ))}
        </div>
      ) : null}

      {/* "How we normalize" reference panel */}
      {showNormalize ? (
        <div
          style={{
            marginTop: T.space3,
            padding: T.space4,
            background: T.nested,
            borderRadius: T.radiusNested,
            fontSize: T.textSm,
            color: T.inkBody,
          }}
        >
          <div style={{ fontWeight: 600, color: T.inkStrong, marginBottom: T.space2 }}>
            How we normalize grades
          </div>
          <div style={{ marginBottom: T.space3, lineHeight: 1.5 }}>
            Every school reports grades on its own scale. Below is how we convert them to a
            single 4.0-scale number for each mode. HS modes honor +/- variants; UC and CSU
            modes collapse +/- to the base letter (per UC and CSU policy).
          </div>
          <table style={{ width: '100%', fontSize: T.textXs, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: `1px solid ${T.hairline}`, color: T.inkStrong }}>
                <th style={{ padding: T.space2 }}>Letter</th>
                <th style={{ padding: T.space2 }}>0-100 range</th>
                <th style={{ padding: T.space2 }}>HS modes (1, 2)</th>
                <th style={{ padding: T.space2 }}>UC / CSU modes (3-6)</th>
              </tr>
            </thead>
            <tbody>
              {[
                { letter: 'A+ / A',  range: '93-100', hs: '4.0', uc: '4.0' },
                { letter: 'A-',      range: '90-92',  hs: '3.7', uc: '4.0 (collapsed)' },
                { letter: 'B+',      range: '87-89',  hs: '3.3', uc: '3.0 (collapsed)' },
                { letter: 'B',       range: '83-86',  hs: '3.0', uc: '3.0' },
                { letter: 'B-',      range: '80-82',  hs: '2.7', uc: '3.0 (collapsed)' },
                { letter: 'C+',      range: '77-79',  hs: '2.3', uc: '2.0 (collapsed)' },
                { letter: 'C',       range: '73-76',  hs: '2.0', uc: '2.0' },
                { letter: 'C-',      range: '70-72',  hs: '1.7', uc: '2.0 (collapsed)' },
                { letter: 'D+ / D',  range: '63-69',  hs: '1.0-1.3', uc: '1.0 (no bonus)' },
                { letter: 'D-',      range: '60-62',  hs: '0.7', uc: '1.0 (no bonus)' },
                { letter: 'F',       range: '0-59',   hs: '0.0', uc: '0.0 (no bonus)' },
              ].map((row) => (
                <tr key={row.letter} style={{ borderBottom: `1px solid ${T.hairline}` }}>
                  <td style={{ padding: T.space2, fontWeight: 600 }}>{row.letter}</td>
                  <td style={{ padding: T.space2, color: T.inkMuted }}>{row.range}</td>
                  <td style={{ padding: T.space2 }}>{row.hs}</td>
                  <td style={{ padding: T.space2 }}>{row.uc}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: T.space3, fontSize: T.textXs, color: T.inkMuted, lineHeight: 1.5 }}>
            A D or F in an AP / IB / Honors course still counts toward the base GPA, but no
            +1.0 honors bonus is applied (grade must be C or better to earn the bonus).
          </div>
        </div>
      ) : null}

      {/* Step 6 — What-if panel */}
      {forecastableCourses.length > 0 ? (
        <div
          style={{
            marginTop: T.space4,
            border: `1px dashed ${T.hairlineStrong}`,
            borderRadius: T.radiusNested,
            padding: T.space3,
          }}
        >
          <div style={{ fontSize: T.textSm, fontWeight: 600, color: T.inkStrong, marginBottom: T.space2 }}>
            What if? Try a grade for an in-progress or planned course
          </div>
          <div style={{ display: 'flex', gap: T.space2, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={selectedCourseKey}
              onChange={(e) => {
                setSelectedCourseKey(e.target.value);
                setSelectedGrade('');
              }}
              style={{
                padding: '6px',
                fontSize: T.textSm,
                minWidth: '220px',
                border: `1px solid ${T.hairlineStrong}`,
                borderRadius: T.radiusControl,
              }}
            >
              <option value="">Select a course…</option>
              {forecastableCourses.map((c) => (
                <option key={courseKey(c)} value={courseKey(c)}>
                  {c.course} · {c.term} ({c.schoolName})
                </option>
              ))}
            </select>
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              disabled={!selectedCourseKey}
              style={{
                padding: '6px',
                fontSize: T.textSm,
                minWidth: '90px',
                border: `1px solid ${T.hairlineStrong}`,
                borderRadius: T.radiusControl,
              }}
            >
              <option value="">Grade…</option>
              {gradeOptionsForSelected.map((g: any) => (
                <option key={g.value} value={g.value}>
                  {g.value}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddWhatIf}
              disabled={!selectedCourseKey || !selectedGrade}
              style={{
                padding: '6px 14px',
                fontSize: T.textSm,
                cursor: 'pointer',
                background: T.accent,
                color: T.inkOnDark,
                border: 'none',
                borderRadius: T.radiusControl,
                fontWeight: 600,
              }}
            >
              Add
            </button>
            {whatIfEntries.length > 0 ? (
              <button
                onClick={() => setWhatIfEntries([])}
                style={{
                  padding: '6px 14px',
                  fontSize: T.textSm,
                  cursor: 'pointer',
                  background: 'transparent',
                  color: T.inkBody,
                  border: `1px solid ${T.hairlineStrong}`,
                  borderRadius: T.radiusControl,
                }}
              >
                Clear all
              </button>
            ) : null}
          </div>

          {whatIfEntries.length > 0 ? (
            <div style={{ marginTop: T.space2, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {whatIfEntries.map((e) => (
                <span
                  key={courseKey(e)}
                  style={{
                    fontSize: T.textXs,
                    background: T.accentWash,
                    color: T.accentInk,
                    borderRadius: T.radiusControl,
                    padding: '4px 8px',
                  }}
                >
                  {e.course} ({e.term}): {e.grade}{' '}
                  <span
                    onClick={() => handleRemoveWhatIf(courseKey(e))}
                    style={{ cursor: 'pointer', color: T.warningInk, marginLeft: '2px' }}
                    role="button"
                    aria-label={`Remove ${e.course}`}
                  >
                    ✕
                  </span>
                </span>
              ))}
            </div>
          ) : null}

          {whatIfLoading ? (
            <div style={{ fontSize: T.textSm, color: T.inkMuted, marginTop: T.space2 }}>Calculating…</div>
          ) : null}

          {!whatIfLoading && whatIfResult ? (
            <div style={{ marginTop: T.space3 }}>
              <div style={{ fontSize: T.textXs, color: T.inkMuted, marginBottom: T.space2, fontWeight: 600 }}>
                Preview impact across all four GPAs
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: T.space2 }}>
                {MODE_ORDER.map((mode) => {
                  const current = gpaForSchool(result?.modes?.[String(mode)], schoolFilter);
                  const whatIf = gpaForSchool(whatIfResult.modes?.[String(mode)], schoolFilter);
                  const hasDelta = current !== null && whatIf !== null;
                  const delta = hasDelta ? (whatIf as number) - (current as number) : null;
                  const deltaSignificant = delta !== null && Math.abs(delta) >= 0.005;
                  return (
                    <div key={mode} style={{ fontSize: T.textSm, color: T.inkBody }}>
                      <strong style={{ color: T.inkStrong }}>{MODE_LONG_LABELS[mode]}:</strong>{' '}
                      {formatGpa(current)} → {formatGpa(whatIf)}
                      {deltaSignificant ? (
                        <span
                          style={{
                            color: (delta as number) > 0 ? T.successInk : T.dangerInk,
                            marginLeft: '4px',
                            fontWeight: 600,
                          }}
                        >
                          ({(delta as number) > 0 ? '+' : ''}
                          {(delta as number).toFixed(2)})
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Breakdown modal */}
      {openMode !== null ? (
        <div style={overlayStyle} onClick={() => setOpenMode(null)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: T.ink, fontSize: T.textXl }}>{MODE_LONG_LABELS[openMode]} breakdown</h3>
              <button
                onClick={() => setOpenMode(null)}
                style={{ border: 'none', background: 'none', fontSize: T.textXl, cursor: 'pointer', color: T.inkMuted }}
                aria-label="Close breakdown"
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: T.textSm, color: T.inkMuted, marginTop: '2px', marginBottom: T.space3 }}>
              {TILE_SCOPE[openMode]}
            </div>
            <div style={{ margin: `${T.space2} 0`, fontSize: T.textMd, color: T.inkStrong }}>
              GPA: <strong style={{ color: T.ink }}>{formatGpa(openModeResult?.gpa)}</strong>{' '}
              <span style={{ color: T.inkMuted, fontWeight: 400 }}>
                ({openModeResult?.courseCount ?? 0} courses)
              </span>
              {CAPPED_MODES.has(openMode) && openModeResult?.bonusCapReached ? (
                <span style={{ color: T.warningInk, marginLeft: T.space2 }}>· bonus cap reached</span>
              ) : null}
            </div>
            <table style={{ width: '100%', fontSize: T.textSm, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: `1px solid ${T.hairline}`, color: T.inkStrong }}>
                  <th style={{ padding: T.space1 }}>Course</th>
                  <th style={{ padding: T.space1 }}>Term</th>
                  <th style={{ padding: T.space1 }}>Grade</th>
                  <th style={{ padding: T.space1 }}>Credit</th>
                  <th style={{ padding: T.space1 }}>Bonus</th>
                </tr>
              </thead>
              <tbody>
                {(openModeResult?.contributingCourses ?? []).map((c) => (
                  <tr key={`${c.gpaForecastId}-${c.term}`} style={{ borderBottom: `1px solid ${T.hairline}` }}>
                    <td style={{ padding: T.space1, color: T.ink }}>{c.course}</td>
                    <td style={{ padding: T.space1, color: T.inkBody }}>{c.term}</td>
                    <td style={{ padding: T.space1, color: T.inkBody }}>{c.rawGrade}</td>
                    <td style={{ padding: T.space1, color: T.inkBody }}>{c.credit}</td>
                    <td style={{ padding: T.space1, color: c.bonusApplied ? T.successInk : T.inkMuted }}>
                      {c.bonusApplied ? '+1.0' : '-'}
                    </td>
                  </tr>
                ))}
                {(openModeResult?.contributingCourses ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: T.space2, color: T.inkMuted }}>
                      No courses counted in this mode.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GPAHeroCard;
